// @systeme: interface
// Une fiche proposée : chaque champ est un bouton qui ouvre le sélecteur adapté, les
// hypothèses incertaines sont en italique avec un point d'interrogation, et la question
// du moment est en bas avec ses réponses en boutons. Un pouce valide tout.
import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { CalendarDays, Clock, Flag, Repeat, Tag, UserRound } from 'lucide-react';
import type { Cadence, CategorieElement, ChampFiche, FicheProposee, MajFiche } from '@foyer/shared';
import { CategoriesElement, Cadences } from '@foyer/shared';
import { api } from '../lib/api';
import { formaterJourCourt } from '../lib/dates';
import { couleurs, tailles } from '../theme/theme';
import { useMessages } from './Messages';

const NOMS_CATEGORIES: Record<CategorieElement, string> = {
  poubelles: 'Poubelles',
  sante: 'Santé',
  administratif: 'Papiers',
  scolaire: 'École',
  maison: 'Maison',
  social: 'Famille, amis',
  courses: 'Courses',
  travail: 'Travail',
  finances: 'Finances',
  autre: 'Autre',
};
const NOMS_TYPES = {
  tache: 'À faire',
  evenement: 'Rendez-vous',
  note: 'Note',
  course: 'À acheter',
} as const;

type Edition =
  | 'titre'
  | 'jour'
  | 'heure'
  | 'personne'
  | 'categorie'
  | 'correction'
  | 'cadence'
  | 'butoir'
  | null;

const NOMS_CADENCES: Record<Cadence, string> = {
  semaine: 'Chaque semaine',
  quinzaine: 'Une semaine sur deux',
  mois: 'Chaque mois',
  annee: 'Chaque année',
  jour: 'Chaque jour',
};
const NOMS_JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

/** « Chaque mardi », « Chaque mois » : la cadence telle qu'on la dit, avec les jours s'ils sont connus. */
function decrireCadence(cadence: Cadence, joursSemaine: number[]): string {
  if ((cadence === 'semaine' || cadence === 'quinzaine') && joursSemaine.length > 0) {
    const jours = joursSemaine.map((j) => NOMS_JOURS[j - 1]).join(' et ');
    return cadence === 'semaine' ? `Chaque ${jours}` : `Un ${jours} sur deux`;
  }
  return NOMS_CADENCES[cadence];
}

interface Props {
  fiche: FicheProposee;
  onChangee(fiche: FicheProposee): void;
  onTerminee(): void;
}

export function CarteFiche({ fiche, onChangee, onTerminee }: Props) {
  const { dire, direErreur } = useMessages();
  const [edition, setEdition] = useState<Edition>(null);
  const [brouillon, setBrouillon] = useState('');
  const [rappels, setRappels] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const d = fiche.donnees;
  const question = fiche.questions[0];

  async function agir(action: () => Promise<FicheProposee | void>, apres?: () => void) {
    setEnCours(true);
    try {
      const resultat = await action();
      if (resultat) onChangee(resultat);
      apres?.();
    } catch (e) {
      direErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  const modifier = (maj: MajFiche) =>
    agir(
      () => api.fiches.modifier(fiche.id, maj),
      () => setEdition(null),
    );
  const repondre = (champ: ChampFiche, valeur: string | null) =>
    agir(() => api.fiches.repondre(fiche.id, { champ, valeur }));

  function ouvrir(champ: Edition, valeurInitiale = '') {
    setBrouillon(valeurInitiale);
    setEdition(champ);
  }

  function preciser(champ: ChampFiche) {
    if (champ === 'date') ouvrir('jour', d.jour ?? '');
    else if (champ === 'heure') ouvrir('heure', d.heure ?? '');
    else if (champ === 'personne') ouvrir('personne', d.personne ?? '');
    else ouvrir('titre', d.titre);
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'divider' }}>
      {/* Le titre : un bouton, comme tout le reste. */}
      {edition === 'titre' ? (
        <Stack direction="row" spacing={1}>
          <TextField
            value={brouillon}
            onChange={(e) => setBrouillon(e.target.value)}
            autoFocus
            inputProps={{ 'aria-label': 'Titre de la fiche', maxLength: 200 }}
          />
          <Button
            variant="contained"
            disabled={enCours || brouillon.trim() === ''}
            onClick={() => void modifier({ titre: brouillon.trim() })}
          >
            OK
          </Button>
        </Stack>
      ) : (
        <Typography
          variant="h3"
          component="h3"
          onClick={() => ouvrir('titre', d.titre)}
          sx={{ cursor: 'pointer', mb: 1 }}
          role="button"
          tabIndex={0}
        >
          {d.titre}
        </Typography>
      )}

      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1 }}>
        <Chip
          icon={<Tag size={16} />}
          label={NOMS_TYPES[d.type]}
          size="small"
          variant="outlined"
          onClick={() => setEdition(edition === 'categorie' ? null : 'categorie')}
        />
        <Chip
          icon={<CalendarDays size={16} />}
          label={
            d.jour
              ? `${d.butoir ? 'dès le ' : ''}${formaterJourCourt(d.jour)}`
              : d.cadence
                ? 'Dès maintenant'
                : 'Sans date'
          }
          size="small"
          variant={d.jour ? 'filled' : 'outlined'}
          onClick={() => ouvrir('jour', d.jour ?? '')}
        />
        {d.type !== 'course' && d.type !== 'note' && (
          <Chip
            icon={<Repeat size={16} />}
            label={d.cadence ? decrireCadence(d.cadence, d.joursSemaine) : 'Une fois'}
            size="small"
            variant={d.cadence ? 'filled' : 'outlined'}
            onClick={() => setEdition(edition === 'cadence' ? null : 'cadence')}
          />
        )}
        {(d.butoir || d.type === 'tache') && (
          <Chip
            icon={<Flag size={16} />}
            label={
              d.butoir
                ? `avant le ${formaterJourCourt(d.butoir)}${d.butoirNom ? ` (${d.butoirNom})` : ''}`
                : 'Date limite ?'
            }
            size="small"
            variant={d.butoir ? 'filled' : 'outlined'}
            onClick={() => ouvrir('butoir', d.butoir ?? '')}
          />
        )}
        {(d.type === 'evenement' || d.heure) && (
          <Chip
            icon={<Clock size={16} />}
            label={d.heure ? d.heure.replace(':', 'h') : 'Heure ?'}
            size="small"
            variant={d.heure ? 'filled' : 'outlined'}
            onClick={() => ouvrir('heure', d.heure ?? '')}
          />
        )}
        {((d.type !== 'course' && d.type !== 'note') || d.personne) && (
          <Chip
            icon={<UserRound size={16} />}
            label={d.personne ?? 'Pour qui ?'}
            size="small"
            variant={d.personne ? 'filled' : 'outlined'}
            onClick={() => ouvrir('personne', d.personne ?? '')}
          />
        )}
        {d.categorie && (
          <Chip
            label={NOMS_CATEGORIES[d.categorie]}
            size="small"
            variant="outlined"
            onClick={() => setEdition(edition === 'categorie' ? null : 'categorie')}
          />
        )}
      </Stack>

      {edition === 'jour' && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField
            type="date"
            value={brouillon}
            onChange={(e) => setBrouillon(e.target.value)}
            inputProps={{ 'aria-label': 'Jour' }}
            autoFocus
          />
          <Button
            variant="contained"
            disabled={enCours || brouillon === ''}
            onClick={() => void modifier({ jour: brouillon })}
          >
            OK
          </Button>
          <Button color="inherit" onClick={() => void modifier({ jour: null })}>
            Sans date
          </Button>
        </Stack>
      )}
      {edition === 'cadence' && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Ça revient ?
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={d.cadence ?? 'aucune'}
            onChange={(_e, valeur: string | null) => {
              if (valeur === null) return;
              void modifier({ cadence: Cadences.find((c) => c === valeur) ?? null });
            }}
            aria-label="Ça revient ?"
            sx={{ flexWrap: 'wrap' }}
          >
            <ToggleButton value="aucune">Une fois</ToggleButton>
            {Cadences.map((c) => (
              <ToggleButton key={c} value={c}>
                {NOMS_CADENCES[c]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}
      {edition === 'butoir' && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField
            type="date"
            value={brouillon}
            onChange={(e) => setBrouillon(e.target.value)}
            inputProps={{ 'aria-label': 'Date limite' }}
            autoFocus
          />
          <Button
            variant="contained"
            disabled={enCours || brouillon === ''}
            onClick={() => void modifier({ butoir: brouillon })}
          >
            OK
          </Button>
          <Button color="inherit" onClick={() => void modifier({ butoir: null })}>
            Pas de limite
          </Button>
        </Stack>
      )}
      {edition === 'heure' && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField
            type="time"
            value={brouillon}
            onChange={(e) => setBrouillon(e.target.value)}
            inputProps={{ 'aria-label': 'Heure' }}
            autoFocus
          />
          <Button
            variant="contained"
            disabled={enCours || brouillon === ''}
            onClick={() => void modifier({ heure: brouillon })}
          >
            OK
          </Button>
          <Button color="inherit" onClick={() => void modifier({ heure: null })}>
            Journée
          </Button>
        </Stack>
      )}
      {edition === 'personne' && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField
            value={brouillon}
            onChange={(e) => setBrouillon(e.target.value)}
            placeholder="Léa, Théo, le dentiste…"
            inputProps={{ 'aria-label': 'Personne', maxLength: 60 }}
            autoFocus
          />
          <Button
            variant="contained"
            disabled={enCours}
            onClick={() => void modifier({ personne: brouillon.trim() || null })}
          >
            OK
          </Button>
        </Stack>
      )}
      {edition === 'categorie' && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField
            select
            label="Genre"
            value={d.type}
            onChange={(e) =>
              void modifier({
                type:
                  e.target.value === 'tache' ||
                  e.target.value === 'evenement' ||
                  e.target.value === 'note' ||
                  e.target.value === 'course'
                    ? e.target.value
                    : 'tache',
              })
            }
            sx={{ minWidth: 150 }}
          >
            {Object.entries(NOMS_TYPES).map(([v, l]) => (
              <MenuItem key={v} value={v}>
                {l}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Catégorie"
            value={d.categorie ?? ''}
            onChange={(e) =>
              void modifier({
                categorie: CategoriesElement.find((c) => c === e.target.value) ?? null,
              })
            }
            fullWidth
          >
            <MenuItem value="">Aucune</MenuItem>
            {CategoriesElement.map((c) => (
              <MenuItem key={c} value={c}>
                {NOMS_CATEGORIES[c]}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      )}

      {d.doutes.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {d.doutes.map((doute) => (
            <Typography
              key={doute}
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: 'italic' }}
            >
              {doute.replace(/[.?]*$/, '')} ?
            </Typography>
          ))}
        </Box>
      )}
      {d.detail && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {d.detail}
        </Typography>
      )}

      {question && (
        <Box
          sx={{ bgcolor: couleurs.saugeClair, borderRadius: `${tailles.rayon}px`, p: 1.5, mb: 1.5 }}
        >
          <Typography sx={{ fontWeight: 600, mb: 1 }}>{question.texte}</Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
            {question.options.map((o) => (
              <Button
                key={o.libelle}
                variant={o.genre === 'plus_tard' ? 'text' : 'outlined'}
                size="small"
                disabled={enCours}
                onClick={() =>
                  o.genre === 'preciser'
                    ? preciser(question.champ)
                    : void repondre(question.champ, o.valeur)
                }
              >
                {o.libelle}
              </Button>
            ))}
          </Stack>
        </Box>
      )}

      {edition === 'correction' && (
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            void agir(
              () => api.fiches.corriger(fiche.id, { texte: brouillon.trim() }),
              () => setEdition(null),
            );
          }}
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" spacing={1}>
            <TextField
              value={brouillon}
              onChange={(e) => setBrouillon(e.target.value)}
              placeholder="Non, c’est mardi prochain à 16h30"
              inputProps={{ 'aria-label': 'Correction', maxLength: 1000 }}
              autoFocus
            />
            <Button type="submit" variant="contained" disabled={enCours || brouillon.trim() === ''}>
              OK
            </Button>
          </Stack>
        </Box>
      )}

      <FormControlLabel
        control={
          <Checkbox checked={rappels} onChange={(e) => setRappels(e.target.checked)} size="small" />
        }
        label="Les rappels habituels"
        sx={{ mb: 0.5 }}
      />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          disabled={enCours}
          onClick={() =>
            void agir(
              () => api.fiches.valider(fiche.id, { rappels }),
              () => {
                dire('C’est noté.');
                onTerminee();
              },
            )
          }
        >
          C’est bon
        </Button>
        <Button color="inherit" disabled={enCours} onClick={() => ouvrir('correction')}>
          Corriger
        </Button>
        <Button
          color="inherit"
          disabled={enCours}
          onClick={() => void agir(() => api.fiches.rejeter(fiche.id), onTerminee)}
        >
          Non, laisse tomber
        </Button>
      </Stack>
    </Paper>
  );
}
